<template>
  <div class="container">
    <div class="card text-center mt-5">
      <div class="card-header bg-warning text-white">
        <h1 class="card-title mb-0">Proof of Operating Time</h1>
      </div>
      <div class="card-body">
        <div class="file-upload">
          <input type="file" class="form-control-file mb-3" ref="fileInput" @change="handleFileUpload"
            accept=".xlsx, .xls" />
          <b-button class="btn btn-success mb-3" @click="downloadZip" :disabled="!isFileUploaded">
            <b-spinner v-if="isProcessing" small type="grow"></b-spinner>
            Download ZIP
          </b-button>
          <div class="progress" v-if="showProgressBar">
            <div class="progress-bar progress-bar-striped bg-success" role="progressbar"
              :style="{ width: progress + '%' }" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">{{
                progress }}%</div>
          </div>
        </div>
        <div v-if="inconsistentPNs.length > 0" class="alert alert-danger mt-3">
          <strong>Warning!</strong> The following PN numbers have inconsistent email addresses:
          <ul>
            <li v-for="pn in inconsistentPNs" :key="pn">{{ pn }}</li>
          </ul>
        </div>
      </div>
      <b-button class="btn btn-success mb-3" to="/sproof">Upload to Sproof</b-button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { generatePDF } from '../utils/pdfGenerator';

const jsonData = ref(null);
const isFileUploaded = ref(false);
const showProgressBar = ref(false);
const progress = ref(0);
const fileInput = ref(null);
const isProcessing = ref(false);
const inconsistentPNs = ref([]);

const handleFileUpload = (event) => {
  isProcessing.value = true;
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', sheetRows: 10000 });
      const worksheet = workbook.Sheets['Einsätze'];
      if (worksheet) {
        let decodedRange = XLSX.utils.decode_range(worksheet['!ref']);
        if (decodedRange.e.r > 10000) {
          decodedRange.e.r = 10000;
        }
        if (!worksheet['A1']) {
          decodedRange.s.r = Math.max(decodedRange.s.r, 1);
        }
        const restrictedRange = XLSX.utils.encode_range(decodedRange);
        
        jsonData.value = XLSX.utils.sheet_to_json(worksheet, { range: restrictedRange, raw: false, cellFormula: false });
        inconsistentPNs.value = checkForInconsistentEmails(jsonData.value);
        isFileUploaded.value = true;
        isProcessing.value = false;
      } else {
        alert("Sheet 'Einsätze' not found!");
        isProcessing.value = false;
      }
    };
    reader.readAsArrayBuffer(file);
  }
};

const checkForInconsistentEmails = (data) => {
  const pnEmails = data.reduce((acc, curr) => {
    if (!acc[curr.PN]) {
      acc[curr.PN] = new Set();
    }
    acc[curr.PN].add(curr.Email);
    return acc;
  }, {});

  return Object.keys(pnEmails).filter(pn => pnEmails[pn].size > 1);
};

const generatePDFsAndZip = async (data) => {
  const zip = new JSZip();

  // Extract unique PN numbers
  const uniquePNs = [...new Set(data.map(item => item.PN))];

  let processedItems = 0;
  const totalItems = uniquePNs.length;

  for (const pn of uniquePNs) {
    const hostData = data.filter(item => item.PN === pn);

    if (hostData.length > 0) {
      const host = {
        Name: hostData[0].Name,
        First: hostData[0].First,
        PN: hostData[0].PN,
        Office: hostData[0].Office,
        Email: hostData[0].Email,
        projects: hostData
      };

      if (host.Email === '0') {
        console.log(`Skipping host: ${host.Name} ${host.First} due to Email being '0'`);
        processedItems++;
        progress.value = Math.round((processedItems / totalItems) * 100);
        continue;
      }

      const pdfData = generatePDF(host);

      if (pdfData) {
        const validDates = host.projects
          .filter(project => parseFloat(project.Delta) !== 0)
          .map(project => project['Date ']);

        const highestMonth = getHighestMonth(validDates);

        const fileName = `${host.Name}_${host.PN}_EZNW_${highestMonth}.pdf`;
        zip.file(fileName, pdfData);
      } else {
        console.log(`PDF not generated for: ${host.Name} ${host.First}`);
      }
    } else {
      console.log(`No data found for PN: ${pn}`);
    }

    processedItems++;
    progress.value = Math.round((processedItems / totalItems) * 100);
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return zip.generateAsync({ type: 'blob' });
};

const getHighestMonth = (dateStrings) => {
  const formats = ['DD.MM.YYYY', 'M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'D/M/YYYY', 'DD/MM/YYYY', 'M/D/YY'];
  let highestMonth = null;

  dateStrings.forEach(dateStr => {
    const date = moment(dateStr, formats, true);
    if (date.isValid()) {
      const monthYear = date.format('MMMM YYYY');
      if (!highestMonth || moment(monthYear, 'MMMM YYYY').isAfter(moment(highestMonth, 'MMMM YYYY'))) {
        highestMonth = monthYear;
      }
    } else {
      console.error(`Invalid date format: ${dateStr}`);
    }
  });

  return highestMonth || 'Invalid Date';
};

const downloadZip = async () => {
  if (jsonData.value) {
    progress.value = 0;
    showProgressBar.value = true;
    isProcessing.value = true;

    const zipContent = await generatePDFsAndZip(jsonData.value);

    const downloadSimulation = setInterval(() => {
      if (progress.value < 100) {
        progress.value += 10;
      } else {
        clearInterval(downloadSimulation);
        saveAs(zipContent, 'hosts.zip');
        showProgressBar.value = false;
        isProcessing.value = false;
        resetFileInput();
      }
    }, 100);
  }
};

const resetFileInput = () => {
  if (fileInput.value) {
    fileInput.value.value = '';
    isFileUploaded.value = false;
  }
};
</script>


<style scoped>
.alert {
  margin-top: 20px;
}

.card-title {
  font-size: 24px;
  font-weight: bold;
}

.card-header {
  background-color: #E2923D;
}

.container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.card {
  width: 60%;
}
</style>
